import rpcShared from '@rockpapercoin/rpc-shared';
const {
	invoices: {
		calculateInvoiceTotal,
		paymentInstallmentAmountDueWithinRange,
		PaymentInstallmentAmountDueRanges,
	},
	strings: { errorMessages },
} = rpcShared;
import { DeepOmit } from '../../lib/helpers/apollo';
import { gql } from '@apollo/client';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
	InstallmentStatus,
	InvoiceStatus,
	PaymentInstallmentDataInput,
	UsePaymentInstallmentsInvoiceEditInvoiceFragment,
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
} from '#types';
import dayjs from 'dayjs';
import dateIsValid from '../../lib/helpers/dateIsValid';
import { paymentInstallmentIsMutable } from './InvoiceEditPaymentInstallments/helpers';

const usePaymentInstallmentsInvoiceEditLineItemFragment = gql`
	fragment usePaymentInstallmentsInvoiceEditLineItem on LineItem {
		id
		name
		rate
		quantity
		taxPercentage
	}
`;

const usePaymentInstallmentsInvoiceEditPaymentInstallmentFragment = gql`
	fragment usePaymentInstallmentsInvoiceEditPaymentInstallment on PaymentInstallment {
		id
		name
		status
		dueDate
		amountDue
		enteredAsPercentageAmount
		percentageDue
	}
`;

export const usePaymentInstallmentsInvoiceEditInvoiceFragment = gql`
	fragment usePaymentInstallmentsInvoiceEditInvoice on Invoice {
		id
		status
		discountAmount
		discountPercentage
		lineItems {
			...usePaymentInstallmentsInvoiceEditLineItem
		}
		paymentInstallments {
			...usePaymentInstallmentsInvoiceEditPaymentInstallment
		}
	}
	${ usePaymentInstallmentsInvoiceEditPaymentInstallmentFragment }
	${ usePaymentInstallmentsInvoiceEditLineItemFragment }
`;

export type PaymentInstallmentDetails = {
	id: string;
	disabled: boolean;
	autoFocus: boolean;
	originalDueDate: Date | string | null;
	validation: Record<
	keyof DeepOmit<
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
	| '__typename'
	| 'id'
	| 'status'
	| 'enteredAsPercentageAmount'
	| 'percentageDue'
	>,
	string
	>;
};

export type OnUpdate = (
	delta: Partial<UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment>
) => void;

export const newItemIdMarker = 'new-payment-installment';
let arbitraryCounter = 0;
const percentagePrecision = 4; // two digest to the left of the decimal, and this value - 2 digits to the right

export const getNewItemId = ( index?: number, setArbitraryCounter?: number ) => {
	if ( typeof index === 'number' && typeof setArbitraryCounter === 'number' ) {
		arbitraryCounter = setArbitraryCounter;
	}
	return `${ newItemIdMarker }-${
		typeof index === 'number' ? index : arbitraryCounter++
	}`;
};

/* Since we're going to be mutating state, and mutates state causes new useEffects, lets make some
helpers to let us know when we DON'T need to update state, to avoid render-loops */
const getPercentageDue = (
	paymentInstallment?: Pick<
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
	'amountDue' | 'percentageDue'
	>,
	lineItemsTotal?: number
) =>
	lineItemsTotal && typeof paymentInstallment?.amountDue === 'number'
		? parseFloat(
			( ( paymentInstallment.amountDue / lineItemsTotal ) * 100 ).toPrecision(
				percentagePrecision
			)
		  )
		: paymentInstallment?.percentageDue || 0;

/**
 * There are basically three paths this helper uses to calculate amountDue in pennies
 * 1. The amounts are evenly spread across installments all calculated via percentage, where the percentages add up to (near) 100%
 * 2. The amount is calculated via percentage but the above "extra" criteria do not match
 * 3. Amount due is not calculated via percentage
 *
 * In both case 1 and case 2 a remainder is calculated and is "back-loaded" to match the migration
 */
const getAmountDue = ( {
	lineItemsTotal,
	paymentInstallment,
	paymentInstallments,
	index,
}: {
	lineItemsTotal: number;
	paymentInstallment: Pick<
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
	'amountDue' | 'percentageDue' | 'enteredAsPercentageAmount'
	>;
	paymentInstallments: Pick<
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
	'amountDue' | 'percentageDue' | 'enteredAsPercentageAmount'
	>[];
	index: number;
} ) => {
	const { amountDue, percentageDue } = paymentInstallment;
	if ( paymentInstallment.enteredAsPercentageAmount ) {
		const allArePercentages = paymentInstallments.every(
			( paymentInstallment ) => paymentInstallment.enteredAsPercentageAmount
		);
		const totalPercentage = paymentInstallments.reduce(
			( acc, { percentageDue } ) =>
				typeof percentageDue === 'number' ? acc + percentageDue : acc,
			0
		);
		const percentagesTotalOneHundredPercent = totalPercentage === 100;
		const totalByPercentageDue = paymentInstallments.reduce(
			( acc, { percentageDue } ) =>
				typeof percentageDue === 'number'
					? acc + Math.round( ( lineItemsTotal * percentageDue ) / 100 )
					: acc,
			0
		);
		const remainderToSpread =
			percentagesTotalOneHundredPercent &&
			totalByPercentageDue !== lineItemsTotal
				? lineItemsTotal - totalByPercentageDue
				: 0;
		const perInstallmentRemainder = Math.round(
			remainderToSpread / paymentInstallments.length
		);
		// "back-load" the remainder to match the behavior of the migration for the "old" invoices
		const thisInstallmentAmountWithoutRemainder = Math.round(
			( lineItemsTotal * ( percentageDue || 0 ) ) / 100
		);
		// if all installments are percentages adding up to (near) 100 and there's a remainder..
		if (
			allArePercentages &&
			percentagesTotalOneHundredPercent &&
			paymentInstallments.length > 1
		) {
			if ( index === paymentInstallments.length - 1 ) {
				const remainderAlreadySpread =
					perInstallmentRemainder * ( paymentInstallments.length - 1 );
				return (
					thisInstallmentAmountWithoutRemainder +
					remainderToSpread -
					remainderAlreadySpread
				);
			} else {
				return thisInstallmentAmountWithoutRemainder + perInstallmentRemainder;
			}
		} else {
			// otherwise, if it's just a non-100% situation calculate the amount from the percentage normally
			return thisInstallmentAmountWithoutRemainder + perInstallmentRemainder;
		}
	} else {
		// since it's not a percentage, just return the amount
		return amountDue || 0;
	}
};

const isNotPaid = (
	paymentInstallment: Pick<
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
	'status'
	>
) =>
	!paymentInstallment.status ||
	paymentInstallment.status === InstallmentStatus.Unpaid;

const isPercentChangeRequired = (
	paymentInstallment: Pick<
	UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
	'status' | 'enteredAsPercentageAmount' | 'amountDue' | 'percentageDue'
	>,
	lineItemsTotal: number
) =>
	( ( isNotPaid( paymentInstallment ) &&
		!paymentInstallment.enteredAsPercentageAmount ) ||
		!isNotPaid( paymentInstallment ) ) &&
	paymentInstallment.percentageDue !==
		getPercentageDue( paymentInstallment, lineItemsTotal );

const isAmountChangeRequired = (
	args: Parameters<typeof getAmountDue>[0] & {
		paymentInstallment: Pick<
		UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
		'status'
		>;
	}
) =>
	isNotPaid( args.paymentInstallment ) &&
	args.paymentInstallment.enteredAsPercentageAmount &&
	args.paymentInstallment.amountDue !== getAmountDue( args );

const isChangeRequired = (
	args: Omit<
	Parameters<typeof isAmountChangeRequired>[0],
	'index' | 'paymentInstallment'
	>
) =>
	args.paymentInstallments.some(
		( paymentInstallment, index ) =>
			isPercentChangeRequired( paymentInstallment, args.lineItemsTotal ) ||
			isAmountChangeRequired( { ...args, paymentInstallment, index } )
	);
const getEmptyPaymentInstallment = (
	name: string,
	amountDue: number,
	lineItemsTotal: number
): DeepOmit<UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment> => ( {
	id: getNewItemId(),
	name: name,
	dueDate: null,
	amountDue,
	enteredAsPercentageAmount: false,
	percentageDue: getPercentageDue( { amountDue }, lineItemsTotal ),
	status: null,
} );

const getEmptyPaymentInstallmentDetails = (
	id: string
): PaymentInstallmentDetails => ( {
	id,
	disabled: false,
	autoFocus: true,
	originalDueDate: null,
	validation: {
		name: '',
		dueDate: '',
		amountDue: '',
	},
} );

export type UsePaymentInstallmentsProps = {
	invoice?: Omit<UsePaymentInstallmentsInvoiceEditInvoiceFragment, 'id'> & {
		id?: string;
	};
	setInvoice: React.Dispatch<
	React.SetStateAction<UsePaymentInstallmentsInvoiceEditInvoiceFragment>
	>;
	skipInstallmentCalculation?: boolean;
};

export const usePaymentInstallments = ( {
	invoice,
	setInvoice,
	skipInstallmentCalculation = false,
}: UsePaymentInstallmentsProps ) => {
	const lineItemsTotal = useMemo(
		() => ( invoice ? calculateInvoiceTotal( invoice ) : 0 ),
		[ invoice ]
	);
	const [ paymentInstallmentsDetails, setPaymentInstallmentsDetails ] = useState<
	PaymentInstallmentDetails[]
	>( [] );

	/* We use a ref here so that when skipInstallmentCalculation changes we don't trigger
	the associated useEffect to run again, as that would obviate the usefulness of the skip
	attempt. */
	const skipInstallmentCalculationRef = useRef( skipInstallmentCalculation );
	useEffect( () => {
		skipInstallmentCalculationRef.current = skipInstallmentCalculation;
	}, [ skipInstallmentCalculation ] );

	const invoicePaymentInstallments = useMemo(
		() => invoice?.paymentInstallments,
		[ invoice ]
	);
	const invoiceStatus = useMemo( () => invoice?.status, [ invoice ] );

	// setPaymentInstallments is a wrapper that does the same as React.Dispatch<React.SetStateAction<UsePaymentInstallmentInvoiceFragment>> but for payment installments only
	const setPaymentInstallments = useCallback(
		(
			props:
			| ( (
				prevState: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment[]
				  ) => UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment[] )
			| UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment[]
		) => {
			if ( typeof props === 'function' ) {
				setInvoice( ( prevState ) => ( {
					...prevState,
					paymentInstallments: props( prevState?.paymentInstallments || [] ),
				} ) );
			} else {
				setInvoice( ( prevState ) => ( {
					...prevState,
					paymentInstallments: props,
				} ) );
			}
		},
		[ setInvoice ]
	);

	/* This useEffect is for updating the percentages/amountDue when the payment installments change or when
	the payment installments change */
	useEffect( () => {
		const makeChange = (
			paymentInstallment: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment,
			paymentInstallments: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment[],
			index: number
		) => ( {
			...paymentInstallment,
			...( isAmountChangeRequired( {
				paymentInstallment,
				paymentInstallments,
				lineItemsTotal,
				index,
			} )
				? {
					amountDue: getAmountDue( {
						paymentInstallment,
						paymentInstallments,
						lineItemsTotal,
						index,
					} ),
				  }
				: {} ),
			...( isPercentChangeRequired( paymentInstallment, lineItemsTotal )
				? {
					percentageDue: getPercentageDue( paymentInstallment, lineItemsTotal ),
				  }
				: {} ),
		} );
		/* Now we can update state, or not update state, using the helpers above without render-looping.
		We use a ref here because when we enable skipInstallmentCalculation, we want to avoid triggering
		this to run again. */
		if ( !skipInstallmentCalculationRef.current ) {
			setPaymentInstallments( ( prevState ) => {
				const newState = isChangeRequired( {
					paymentInstallments: prevState,
					lineItemsTotal,
				} )
					? prevState.map( ( paymentInstallment, index ) =>
						makeChange( paymentInstallment, prevState, index )
					  )
					: prevState;
				// we're only supposed to re-sort by dueDate on change when all installments have a non-null value
				const shouldReSort = prevState.every(
					( paymentInstallment ) => !!paymentInstallment.dueDate
				);
				if ( shouldReSort ) {
					// We could do this without the new variable if we used node-20/toSorted()
					const getTime = ( d?: Date | string | null ) =>
						d
							? typeof d === 'string'
								? new Date( d ).getTime()
								: d.getTime()
							: 1;
					[ ...newState ].sort( ( a, b ) => getTime( a.dueDate ) - getTime( b.dueDate ) );
					return newState;
				} else {
					return newState;
				}
			} );
		}
		setPaymentInstallmentsDetails(
			invoicePaymentInstallments?.map( ( paymentInstallment ) => {
				const paymentInstallmentDetail = getEmptyPaymentInstallmentDetails(
					paymentInstallment.id
				);
				paymentInstallmentDetail.disabled =
					!paymentInstallmentIsMutable( paymentInstallment );
				paymentInstallmentDetail.autoFocus = false;
				paymentInstallmentDetail.originalDueDate =
					paymentInstallment.dueDate || null;
				return paymentInstallmentDetail;
			} ) || []
		);
	}, [
		lineItemsTotal,
		setPaymentInstallments,
		invoicePaymentInstallments
	] );

	const onAddPaymentInstallments = useCallback(
		( numberOfInstallments?: number ) => {
			const remainingBalance =
				lineItemsTotal -
				( invoicePaymentInstallments?.reduce(
					( acc, paymentInstallment ) =>
						typeof paymentInstallment.amountDue === 'number'
							? acc + paymentInstallment.amountDue
							: 0,
					0
				) || 0 );
			if ( typeof numberOfInstallments === 'number' ) {
				// we're adding 1-10 installments all at once
				const newInstallments =
					numberOfInstallments === 1
						? [
							getEmptyPaymentInstallment(
								'Total Payment',
								remainingBalance,
								lineItemsTotal
							),
						  ]
						: [ ...Array( numberOfInstallments ).keys() ].map( ( counter ) =>
							getEmptyPaymentInstallment(
								`Installment ${ counter + 1 }`,
								Math.round( remainingBalance / numberOfInstallments ),
								lineItemsTotal
							)
						  );
				// now lets account for any remainder by adjusting the last installment
				const allButLastInstallmentTotal = newInstallments
					.slice( 0, numberOfInstallments - 1 )
					.reduce(
						( acc, paymentInstallment ) =>
							paymentInstallment.amountDue
								? acc + paymentInstallment.amountDue
								: acc,
						0
					);
				newInstallments[ newInstallments.length - 1 ].amountDue =
					remainingBalance - allButLastInstallmentTotal;
				setPaymentInstallments( ( prevState ) => [ ...prevState, ...newInstallments, ] );
			} else {
				// We're just adding one installment at a time
				setPaymentInstallments( ( prevState ) => {
					const newInstallment = getEmptyPaymentInstallment(
						`Installment ${ prevState.length + 1 }`,
						remainingBalance,
						lineItemsTotal
					);
					setPaymentInstallmentsDetails( ( prevState ) => [ ...prevState, getEmptyPaymentInstallmentDetails( newInstallment.id ), ] );
					return [ ...prevState, newInstallment ];
				} );
			}
		},
		[
			invoicePaymentInstallments,
			lineItemsTotal,
			setPaymentInstallments
		]
	);

	const onRemovePaymentInstallment = useCallback(
		( id: string ) => {
			setPaymentInstallments( ( prevState ) =>
				prevState.filter( ( paymentInstallment ) => paymentInstallment.id !== id )
					.length === 0
					? []
					: prevState.filter(
						( paymentInstallment ) => paymentInstallment.id !== id
					  )
			);
			setPaymentInstallmentsDetails( ( prevState ) =>
				prevState.filter(
					( paymentInstallmentDetails ) => paymentInstallmentDetails.id !== id
				).length === 0
					? []
					: prevState.filter(
						( paymentInstallmentDetails ) => paymentInstallmentDetails.id !== id
					  )
			);
		},
		[ setPaymentInstallments ]
	);

	// A type check helper so we can tell if a prop that can be multiple types qualifies as one of those multiple types
	const paymentInstallmentTypeCheck = (
		paymentInstallment:
		| UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
		| PaymentInstallmentDataInput
	): paymentInstallment is PaymentInstallmentDataInput =>
		typeof paymentInstallment.amountDue === 'number' &&
		( [ 'Date', 'string' ].includes( typeof paymentInstallment.dueDate ) ||
			paymentInstallment.dueDate === null ) &&
		typeof paymentInstallment.name === 'string';

	// Strip out the "fake" ids, the ones we use for currently-client-sid-only items
	const paymentInstallmentsInput: PaymentInstallmentDataInput[] = useMemo(
		() =>
			invoicePaymentInstallments?.reduce( ( acc, rawPaymentInstallment ) => {
				// we need to filter down the payment installment from usePaymentInstallments to just these keys
				const inputKeys: ( keyof PaymentInstallmentDataInput )[] = [
					'amountDue',
					'dueDate',
					'enteredAsPercentageAmount',
					'id',
					'name',
					'percentageDue',
				];
				const paymentInstallment = Object.entries( rawPaymentInstallment ).reduce(
					( acc, [ key, value ] ) =>
						inputKeys.includes( key as keyof PaymentInstallmentDataInput )
							? { ...acc, [ key ]: value }
							: acc,
					{} as
						| UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
						| PaymentInstallmentDataInput
				);
				const { id, ...restOfPaymentInstallment } = paymentInstallment;
				// now that we have reduced the large object to fit what GQL is expecting, we can proceed
				if (
					id?.startsWith( newItemIdMarker ) &&
					paymentInstallmentTypeCheck(
						restOfPaymentInstallment as
							| UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
							| PaymentInstallmentDataInput
					)
				) {
					return [ ...acc, { ...restOfPaymentInstallment } as PaymentInstallmentDataInput, ];
				} else if (
					paymentInstallmentTypeCheck(
						paymentInstallment as
							| UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
							| PaymentInstallmentDataInput
					)
				) {
					return [ ...acc, { ...paymentInstallment } as PaymentInstallmentDataInput, ];
				} else {
					return acc;
				}
			}, [] as PaymentInstallmentDataInput[] ) || [],
		[ invoicePaymentInstallments ]
	);

	/* we need to remember our last "intent", like, did we last try to save?
	if so, keep using that validation. */
	const lastValidationIntentRef = useRef( false );
	// Save the information that we tried to save or send so we know to validate on each key press
	const validationEnabledRef = useRef( false );

	const validate = useCallback(
		( intendToSend?: boolean ) => {
			if ( typeof intendToSend === 'boolean' ) {
				lastValidationIntentRef.current = intendToSend;
				validationEnabledRef.current = true;
			}
			// if a save or send button hasn't been clicked yet, don't do this validation, just bail out
			if ( !validationEnabledRef.current ) {
				return true;
			}
			const updatePaymentInstallmentValidation = (
				id: string,
				field: keyof PaymentInstallmentDetails['validation'],
				value: string
			) =>
				setPaymentInstallmentsDetails( ( prevState ) =>
					prevState.map( ( paymentInstallmentDetails ) =>
						paymentInstallmentDetails.id === id
							? {
								...paymentInstallmentDetails,
								validation: {
									...paymentInstallmentDetails.validation,
									[ field ]: value,
								},
							  }
							: paymentInstallmentDetails
					)
				);
			const dateDiffersFromDatabase = ( paymentInstallment: {
				id: string;
				dueDate?: Date | string | null;
			} ) => {
				const existingDueDate = paymentInstallmentsDetails?.find(
					( pi ) => pi.id === paymentInstallment.id
				)?.originalDueDate;
				const idIsClientSideOnly =
					paymentInstallment.id.startsWith( newItemIdMarker );
				if (
					!idIsClientSideOnly &&
					existingDueDate &&
					paymentInstallment.dueDate &&
					dayjs( existingDueDate ).isSame( paymentInstallment.dueDate, 'date' )
				) {
					return false;
				} else {
					return true;
				}
			};
			const dateIsValidCheck = (
				paymentInstallment: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
			) =>
				!paymentInstallment.dueDate ||
				( !!paymentInstallment.dueDate &&
					dateIsValid( paymentInstallment.dueDate ) );
			if (
				intendToSend ||
				lastValidationIntentRef.current ||
				( invoiceStatus && invoiceStatus !== InvoiceStatus.Draft )
			) {
				const nameCheck = (
					paymentInstallment: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
				) => !!paymentInstallment.name;
				const dateExistsCheck = (
					paymentInstallment: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
				) => !!paymentInstallment.dueDate;

				const dateStatusVsPastCheckIntendToSend = (
					paymentInstallment: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment
				) =>
					// if it's not a date we've changed client-side so far, then it's fine
					!dateDiffersFromDatabase( paymentInstallment ) && !intendToSend
						? true
						: // otherwise, if the payment installment is new (no status)
						  ( ( !paymentInstallment.status ||
								// or not new but unpaid
								paymentInstallment.status === InstallmentStatus.Unpaid ) &&
								// and it's due after today
								dayjs( paymentInstallment.dueDate ).isAfter( dayjs(), 'date' ) ) ||
						  // or if the installment is not new and it's not Unpaid
						  ( paymentInstallment.status &&
								paymentInstallment.status !== InstallmentStatus.Unpaid );
				// if we intend to send this invoice, then all payment installments need to have a name
				invoicePaymentInstallments?.forEach( ( paymentInstallment ) => {
					updatePaymentInstallmentValidation(
						paymentInstallment.id,
						'name',
						!nameCheck( paymentInstallment )
							? 'Name is required in order to send invoices'
							: ''
					);
					const amountDueRange =
						paymentInstallmentAmountDueWithinRange( paymentInstallment );
					updatePaymentInstallmentValidation(
						paymentInstallment.id,
						'amountDue',
						amountDueRange === PaymentInstallmentAmountDueRanges.Low
							? errorMessages.paymentInstallmentAmountDueMustBeGreaterThan
							: amountDueRange === PaymentInstallmentAmountDueRanges.High
								? errorMessages.paymentInstallmentAmountDueMustBeLessThan
								: ''
					);
					updatePaymentInstallmentValidation(
						paymentInstallment.id,
						'dueDate',
						!dateExistsCheck( paymentInstallment )
							? 'Due date is required to send invoices'
							: !dateIsValidCheck( paymentInstallment )
								? 'Due date is not a valid date'
								: !dateStatusVsPastCheckIntendToSend( paymentInstallment )
									? 'Unpaid installments require future due dates'
									: ''
					);
				} );
				const allPaymentInstallmentsHaveValidDueDate =
					invoicePaymentInstallments?.every(
						( paymentInstallment ) =>
							dateExistsCheck( paymentInstallment ) &&
							dateStatusVsPastCheckIntendToSend( paymentInstallment )
					);
				const allPaymentInstallmentsHaveName =
					invoicePaymentInstallments?.every( nameCheck );
				if (
					!allPaymentInstallmentsHaveName ||
					!allPaymentInstallmentsHaveValidDueDate
				) {
					return false;
				}
			} else {
				// make sure we clear validation if necessary
				let installmentsBeValid = true;
				invoicePaymentInstallments?.forEach( ( paymentInstallment ) => {
					updatePaymentInstallmentValidation( paymentInstallment.id, 'name', '' );
					const amountIsPositive =
						typeof paymentInstallment.amountDue === 'number' &&
						paymentInstallment.amountDue >= 0;
					updatePaymentInstallmentValidation(
						paymentInstallment.id,
						'amountDue',
						!amountIsPositive ? 'Amount must be a positive amount' : ''
					);
					const dateIsValid = dateIsValidCheck( paymentInstallment );
					updatePaymentInstallmentValidation(
						paymentInstallment.id,
						'dueDate',
						!dateIsValid ? 'Due date is not a valid date' : ''
					);
					if ( !dateIsValid || !amountIsPositive ) {
						installmentsBeValid = false;
					}
				} );
				if ( !installmentsBeValid ) {
					return false;
				}
			}
			const allNumericValuesAreWithinRange = intendToSend
				? invoicePaymentInstallments?.every(
					( paymentInstallment ) =>
						paymentInstallmentAmountDueWithinRange( paymentInstallment ) ===
							PaymentInstallmentAmountDueRanges.WithinRange
				  )
				: invoicePaymentInstallments?.every(
					( paymentInstallment ) =>
						typeof paymentInstallment.amountDue === 'number' &&
							paymentInstallment.amountDue >= 0
				  );
			if ( !allNumericValuesAreWithinRange ) {
				return false;
			}
			return true;
		},
		[
			invoicePaymentInstallments,
			invoiceStatus,
			paymentInstallmentsDetails
		]
	);
	// because of the render cycle, onBlur we need to wait one cycle for payment installments to update so we can validate
	const [ validateOnNextRenderCycle, setValidateOnNextRenderCycle ] =
		useState( false );
	useEffect( () => {
		if ( validateOnNextRenderCycle ) {
			setValidateOnNextRenderCycle( false );
			validate();
		}
	}, [ validate, validateOnNextRenderCycle ] );

	const onUpdatePaymentInstallment = useCallback(
		( id: string ): OnUpdate =>
			( delta ) => {
				setPaymentInstallments( ( prevState ) =>
					prevState.map( ( paymentInstallment ) =>
						paymentInstallment.id === id
							? { ...paymentInstallment, ...delta }
							: paymentInstallment
					)
				);
			},
		[ setPaymentInstallments ]
	);

	return {
		paymentInstallmentsDetails,
		onAddPaymentInstallments,
		onRemovePaymentInstallment,
		onUpdatePaymentInstallment,
		paymentInstallmentsInput,
		validate,
		onBlur: () => setValidateOnNextRenderCycle( true ),
		// replacePaymentInstallments is used with applying invoice templates only
		replacePaymentInstallments: (
			newPaymentInstallments?: UsePaymentInstallmentsInvoiceEditPaymentInstallmentFragment[]
		) => {
			if ( newPaymentInstallments ) {
				/* not setting payment installments here because we want to avoid a render-loop, so we set them
				when we apply the template in useInvoiceEdit */
				setPaymentInstallmentsDetails(
					newPaymentInstallments?.map( ( pi ) =>
						getEmptyPaymentInstallmentDetails( pi.id )
					) || []
				);
			} else {
				const newPaymentInstallment = getEmptyPaymentInstallment(
					'Total Payment',
					lineItemsTotal,
					lineItemsTotal
				);
				setPaymentInstallmentsDetails( [ getEmptyPaymentInstallmentDetails( newPaymentInstallment.id ), ] );
			}
		},
	};
};

export type UsePaymentInstallments = ReturnType<typeof usePaymentInstallments>;
